/**
 * Mapping Logic — alias-matching algorithm.
 *
 * Strategy:
 *   1. Normalize headers (lowercase, strip punctuation/units like "(km)" or
 *      "$/kWh", collapse whitespace).
 *   2. For each schema field, score each header using:
 *        a. Exact normalized match → score 100 → confidence "auto"
 *        b. Alias starts-with / equality on normalized form → 80 → "auto"
 *        c. Alias substring match → 60 → "guess"
 *        d. Levenshtein-like token overlap → 30 → "guess"
 *      Higher score wins. Ties broken by alias order.
 *   3. A header that already won another field cannot be claimed again
 *      (the user can manually re-assign in the UI).
 *   4. Fields with no winner are marked "unmapped" unless `derive` is
 *      satisfied (all `from` keys mapped) — then they become "derived".
 *
 * This module is pure — no React, no DOM. Easy to unit test.
 */
import type { FieldSpec, MappingConfidence, MappingTable, RequiredFieldSchema } from "./types";

/** Normalize a header or alias for comparison. */
export const normalize = (s: string): string =>
  (s || "")
    .toLowerCase()
    // Strip common unit suffixes in parens like "(km)" or "(kwh)".
    .replace(/\([^)]*\)/g, " ")
    // Strip currency / per-unit punctuation: "$/kwh" → " kwh".
    .replace(/[$€£¥]/g, " ")
    .replace(/[\/_\-.,;:]+/g, " ")
    // Collapse whitespace.
    .replace(/\s+/g, " ")
    .trim();

const tokens = (s: string): string[] => normalize(s).split(" ").filter(Boolean);

/**
 * Score how well `header` matches `aliases`.
 * Returns 0 when nothing matches.
 */
const scoreHeaderAgainstAliases = (header: string, aliases: string[]): { score: number; confidence: MappingConfidence } => {
  const nHeader = normalize(header);
  if (!nHeader) return { score: 0, confidence: "unmapped" };

  const headerTokens = new Set(tokens(header));

  for (let i = 0; i < aliases.length; i++) {
    const nAlias = normalize(aliases[i]);
    if (!nAlias) continue;
    // a. Exact match
    if (nHeader === nAlias) return { score: 100 - i, confidence: "auto" };
  }
  for (let i = 0; i < aliases.length; i++) {
    const nAlias = normalize(aliases[i]);
    if (!nAlias) continue;
    // b. Equality on token sets, regardless of order.
    const aliasTokens = new Set(tokens(aliases[i]));
    if (aliasTokens.size === headerTokens.size) {
      let allMatch = true;
      for (const t of aliasTokens) if (!headerTokens.has(t)) { allMatch = false; break; }
      if (allMatch) return { score: 80 - i, confidence: "auto" };
    }
    // c. Substring containment in either direction.
    if (nHeader.includes(nAlias) || nAlias.includes(nHeader)) {
      return { score: 60 - i, confidence: "guess" };
    }
  }
  // d. Token overlap fallback.
  let overlap = 0;
  for (const alias of aliases) for (const t of tokens(alias)) if (headerTokens.has(t)) overlap++;
  if (overlap > 0) return { score: 30 + overlap, confidence: "guess" };
  return { score: 0, confidence: "unmapped" };
};

/**
 * Build an initial mapping table from a schema + observed CSV headers.
 * Each header can only win ONE field — higher-scoring fields claim first.
 */
export function autoMap(schema: RequiredFieldSchema, headers: string[]): MappingTable {
  type Candidate = { field: FieldSpec; header: string; score: number; confidence: MappingConfidence };
  const candidates: Candidate[] = [];
  for (const field of schema.fields) {
    for (const header of headers) {
      const { score, confidence } = scoreHeaderAgainstAliases(header, field.aliases);
      if (score > 0) candidates.push({ field, header, score, confidence });
    }
  }
  // Highest score first; on tie prefer the field's earlier-listed alias (already baked into score).
  candidates.sort((a, b) => b.score - a.score);

  const claimedHeaders = new Set<string>();
  const claimedFields = new Set<string>();
  const table: MappingTable = {};
  for (const c of candidates) {
    if (claimedHeaders.has(c.header) || claimedFields.has(c.field.key)) continue;
    table[c.field.key] = { fieldKey: c.field.key, header: c.header, confidence: c.confidence };
    claimedHeaders.add(c.header);
    claimedFields.add(c.field.key);
  }
  // Fill in unmapped fields (and detect derivable ones).
  for (const field of schema.fields) {
    if (table[field.key]) continue;
    if (field.derive && field.derive.from.every((k) => table[k])) {
      table[field.key] = { fieldKey: field.key, header: null, confidence: "derived" };
    } else {
      table[field.key] = { fieldKey: field.key, header: null, confidence: "unmapped" };
    }
  }
  return table;
}

/**
 * User overrode a single field's mapping (or cleared it).
 * Re-evaluates `derived` status for fields that depend on this one.
 */
export function setMapping(
  schema: RequiredFieldSchema,
  table: MappingTable,
  fieldKey: string,
  header: string | null
): MappingTable {
  const next: MappingTable = { ...table };
  // Free any other field currently holding this header.
  if (header) {
    for (const k of Object.keys(next)) {
      if (k !== fieldKey && next[k].header === header) {
        const fs = schema.fields.find((f) => f.key === k);
        if (fs?.derive && fs.derive.from.every((dep) => dep === fieldKey ? !!header : !!next[dep]?.header)) {
          next[k] = { fieldKey: k, header: null, confidence: "derived" };
        } else {
          next[k] = { fieldKey: k, header: null, confidence: "unmapped" };
        }
      }
    }
  }
  next[fieldKey] = {
    fieldKey,
    header,
    confidence: header ? "manual" : "unmapped",
  };
  // Re-evaluate derived fields whose dependencies changed.
  for (const fs of schema.fields) {
    if (fs.key === fieldKey) continue;
    const cur = next[fs.key];
    if (!cur || cur.header) continue;
    if (fs.derive && fs.derive.from.every((dep) => !!next[dep]?.header)) {
      next[fs.key] = { fieldKey: fs.key, header: null, confidence: "derived" };
    } else if (cur.confidence === "derived") {
      next[fs.key] = { fieldKey: fs.key, header: null, confidence: "unmapped" };
    }
  }
  return next;
}

/**
 * Validate the mapping table — every required field must have either a
 * mapped header OR a satisfied derivation.
 */
export function findMissingRequirements(schema: RequiredFieldSchema, table: MappingTable): string[] {
  const missing: string[] = [];
  for (const f of schema.fields) {
    if (!f.required) continue;
    const m = table[f.key];
    if (!m) { missing.push(f.label); continue; }
    if (m.confidence === "derived") continue; // satisfied via derivation
    if (!m.header) missing.push(f.label);
  }
  return missing;
}
